<template>
  <div class="categoryPage page">
    <div class="container">
      <search-template
        v-if="category"
        list-name="Categories list"
        :default-category-id="category.id"
        :search-page="true"
        route-name="category-slug"
        route-pagination-name="category-page-number"
        :seo-info="htmlDescription"
        :json-description="jsonDescription"
      >
        <template #infoBox v-if="previousUrl">
          <div class="categoryPage__info">
            <info-bar
              v-if="previousUrl"
              :with-close="true"
              color="#ff6e00"
              icon="warning"
              layout="light"
              message="Przepraszamy, ten produkt nie jest już w naszej ofercie.<br />Poniżej możesz znaleźć podobne produkty."
              @close="closeInfo"
            />
          </div>
        </template>

        <template #searchContent v-if="category">
          <div class="categoryPage__contentSearch" v-if="category.id === 10540">
            <search-toners-inks title="Wyszukaj" />
          </div>
          <div
            class="categoryPage__topSeo"
            v-if="htmlMarketingContents && htmlMarketingContents.length"
          >
            <cms-wysiwyg
              :html="section"
              :key="sectionIndex"
              v-for="(section, sectionIndex) in htmlMarketingContents"
            />
          </div>
          <div
            v-if="categories && categories.length && !$device.isMobile"
            class="categoryPage__section categoryPage__section--smallPadding"
          >
            <div class="categoryPage__header">
              <h2 class="categoryPage__headerTitle">Kategorie</h2>
              <p class="categoryPage__headerSubtitle">w {{ category.name }}</p>
            </div>
            <div class="categoryPage__tiles">
              <categories-with-list-slider :items="categories" />
            </div>
          </div>
        </template>
      </search-template>
    </div>
    <LazyHydrate :when-visible="{ rootMargin: '100px' }">
      <newsletter-home white />
    </LazyHydrate>
  </div>
</template>
<script>
import LazyHydrate from 'vue-lazy-hydration';
import { mapMutations, mapState } from 'vuex';
// import MobileBreadcrumbs from '~/components/MobileBreadcrumbs';
import VButton from '~/components/VButton';
import SeoBox from '~/components/SeoBox';
import PageTitle from '~/components/PageTitle';
import SearchTemplate from '~/components/SearchTemplate';
import ContentsKey from '~/components/ContentsKey';
import ContentsSection from '~/components/ContentsSection';
import CmsWysiwyg from '~/components/Cms/CmsWysiwyg';
import SearchTonersInks from '~/containers/Search/SearchTonersInks';
import EditorSearchSeoSections from '@/components/Editor/SearchSeo/EditorSearchSeoSections';
import SidebarMenu from '@/components/SidebarMenu';
import CategoriesWithListSlider from '@/components/CategoriesWithListSlider';
import InfoBar from '~/components/InfoBar';

export default {
  name: 'CategoryPage',
  components: {
    CategoriesWithListSlider,
    SidebarMenu,
    EditorSearchSeoSections,
    SearchTonersInks,
    CmsWysiwyg,
    ContentsSection,
    ContentsKey,
    LazyHydrate,
    SearchTemplate,
    NewsletterHome: () => import('~/components/Newsletter/NewsletterHome'),
    VButton,
    SeoBox,
    PageTitle,
    InfoBar,
    // MobileBreadcrumbs,
  },
  async asyncData({ app, $config, error, $axios, route, redirect }) {
    try {
      const {
        data: {
          category,
          breadcrumbs,
          seo,
          htmlDescription,
          htmlMarketingContents,
          jsonDescription,
          category: { id },
        },
      } = await $axios.get(`${$config.baseOfixApi}/e-commerce/v1/categories/${route.params.slug}`, {
        useCache: true,
      });

      if (category.slug !== route.params.slug) {
        redirect(301, app.localePath({ name: 'category-slug', params: { slug: category.slug } }));
      }

      if (!category.showCategoryView) {
        return {
          category,
          htmlDescription,
          htmlMarketingContents,
          jsonDescription,
          breadcrumbs,
          seo,
        };
      } else {
        const {
          data: {
            categories,
            categories: { children },
          },
        } = await $axios.get(`${$config.baseOfixApi}/e-commerce/v1/categories/tree`, {
          params: {
            parentId: id,
            level: category.level + 1,
            nestingLevel: 1,
            includeRecommendedBrands: false,
            includeRecommendedItem: false,
            orderBy: 'popularity',
          },
          useCache: true,
        });

        const categoriesLinks = categories.map(item => {
          return {
            name: item.name,
            slug: {
              value: item.slug,
              name: 'category-slug',
            },
          };
        });

        return {
          category,
          seo,
          breadcrumbs,
          htmlDescription,
          htmlMarketingContents,
          jsonDescription,
          categories,
          categoriesLinks,
          children,
        };
      }
    } catch (e) {
      if (e.response && e.response.data && e.response.status === 308 && e.response.data.message) {
        redirect(
          301,
          app.localePath({ name: 'category-slug', params: { slug: e.response.data.message } }),
        );
      }
      error({ statusCode: 404, message: 'Category not found' });
    }
  },
  nuxtI18n: {
    paths: {
      pl: '/kategoria/:slug',
      en: '/category/:slug',
    },
  },
  data() {
    return {
      content: '',
      categories: [],
      selectedItem: null,
      breadcrumbs: [],
      category: null,
      previousUrl: null,
    };
  },
  head() {
    return {
      title: this.seo.title,
      meta: this.$helper.metaDescription([
        ...(Object.keys(this.$route.query).length
          ? [
              {
                name: 'robots',
                content: 'noindex, nofollow',
              },
            ]
          : [
              {
                name: 'robots',
                content: 'index, follow',
              },
            ]),
        ...this.seo.metaTags,
      ]),
      script: [...this.$helper.schemaData(this.seo.schemaOrgData)],
      link: this.$helper.prevNextUrl(
        this.$route.query,
        this.pagination.offset,
        this.pagination.limit,
        this.pagination.countTotal,
        'promotions-slug',
        'promotions-page-number',
      ),
    };
  },
  computed: {
    ...mapState({
      pagination: state => state.productsFilters.search.pagination,
    }),
  },
  mounted() {
    if (this.category.forAdults) {
      this.setAgeInformationPopupShow(true);
    }
    const previousUrlCookie = this.$cookies.get('previous-url');
    if (previousUrlCookie) {
      this.previousUrl = `${this.$config.baseOfixUrl}${previousUrlCookie}`;

      this.$cookies.remove('previous-url');
    }
    if (this.$nuxt.$loading._isVue && this.category.showCategoryView) {
      if (this.$nuxt.$loading.finishCreate() && this.category.showCategoryView) {
        this.$nuxt.$loading.finishCreate();
      }
    }
  },
  methods: {
    ...mapMutations({
      setAgeInformationPopupShow: 'layout/setAgeInformationPopupShow',
    }),
    closeInfo() {
      this.previousUrl = '';
    },
  },
};
</script>
<style lang="scss">
.categoryPage {
  $root: &;
  // overflow: hidden;

  &__tiles {
    margin-top: 3rem;
  }

  &__info {
    margin-bottom: 1.6rem;
  }

  @include rwd('tablet') {
    overflow-x: hidden;
  }

  &__section {
    background-color: $cWhite;
    padding: 2.5rem 3.5rem 3rem 3.5rem;
    margin-bottom: 1.6rem;

    &--smallPadding {
      padding: 2rem 2rem 3rem 2rem;
    }
  }

  &__header {
    text-align: left;
  }

  &__headerTitle {
    font-size: 3.6rem;
    line-height: 4.5rem;
    font-weight: 600;
    color: $cMainDark;

    @include rwd('tablet') {
      font-size: 3rem;
      line-height: 3.6rem;
    }
  }

  &__headerSubtitle {
    font-size: 1.6rem;
    line-height: 2rem;
  }

  &__topSeo {
    margin-bottom: 1.6rem;
    background-color: $cWhite;
    padding: 2rem;
  }

  &__contentSearch {
    background: $cWhite;
    margin-bottom: 1.6rem;
    padding: 2rem;
  }
}
</style>
